import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, BookOpen, Newspaper, Building2, Target } from 'lucide-react';

const Home = () => {
  return (
    <div className="bg-[#020617] text-white min-h-screen font-sans selection:bg-indigo-500/30">
      
      {/* --- HERO SECTION --- */}
      <section className="relative pt-20 pb-16 px-6 overflow-hidden">
        {/* Ambient Background Glows */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-indigo-600/20 blur-[120px] rounded-full"></div>
        
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-indigo-500/30 bg-indigo-500/10 text-indigo-400 text-xs font-bold uppercase tracking-widest mb-8 animate-fade-in">
            <Sparkles size={14} /> The Intelligence Era is Here
          </div>
          
          <h1 className="text-5xl md:text-8xl font-black tracking-tighter mb-8 leading-[1.1]">
            Master the <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-cyan-400 to-emerald-400">AI Frontier.</span>
          </h1>
          
          <p className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            The definitive platform for the next generation of AI talent. Connect with industry leaders, access elite learning, and track the evolution of intelligence.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/" className="group bg-white text-slate-950 px-8 py-4 rounded-full font-bold text-lg hover:bg-indigo-50 transition-all flex items-center justify-center gap-2">
              View Opportunities <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* --- BENTO GRID SECTION (Feature Highlights) --- */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            
            {/* 1. Upskill Card (Large) */}
            <div className="md:col-span-8 group relative bg-slate-900/50 border border-slate-800 p-8 rounded-[2.5rem] overflow-hidden hover:border-indigo-500/50 transition-all">
              <div className="relative z-10">
                <div className="w-12 h-12 bg-indigo-500/20 text-indigo-400 rounded-xl flex items-center justify-center mb-6">
                  <BookOpen size={24} />
                </div>
                <h3 className="text-3xl font-bold mb-4">Upskill for the Future</h3>
                <p className="text-slate-400 text-lg max-w-md mb-8">
                  From Andrej Karpathy's LLM deep-dives to Stanford's elite research papers. We curate the world's best AI curriculum for you.
                </p>
                <Link to="/academy" className="inline-flex items-center gap-2 text-indigo-400 font-bold hover:text-indigo-300">
                  Enter Academy <ArrowRight size={18} />
                </Link>
              </div>
              <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-indigo-600/10 blur-3xl rounded-full group-hover:bg-indigo-600/20 transition-all"></div>
            </div>

            {/* 2. Top AI Companies (Vertical) */}
            <div className="md:col-span-4 bg-gradient-to-br from-slate-900 to-slate-950 border border-slate-800 p-8 rounded-[2.5rem] flex flex-col justify-between hover:border-emerald-500/50 transition-all">
              <div>
                <div className="w-12 h-12 bg-emerald-500/20 text-emerald-400 rounded-xl flex items-center justify-center mb-6">
                  <Building2 size={24} />
                </div>
                <h3 className="text-2xl font-bold mb-4">Top AI Companies</h3>
                <p className="text-slate-400">Direct directory of 500+ global industry leaders from OpenAI to NVIDIA.</p>
              </div>
              <Link to="/directory" className="mt-8 text-emerald-400 font-bold flex items-center gap-2">
                Explore Directory <ArrowRight size={18} />
              </Link>
            </div>

            {/* 3. AI News & Trends (Small) */}
            <div className="md:col-span-6 bg-slate-900/50 border border-slate-800 p-8 rounded-[2.5rem] flex flex-col md:flex-row items-center gap-6 hover:border-cyan-500/50 transition-all">
              <div className="flex-1">
                <div className="w-12 h-12 bg-cyan-500/20 text-cyan-400 rounded-xl flex items-center justify-center mb-6">
                  <Newspaper size={24} />
                </div>
                <h3 className="text-2xl font-bold mb-2">AI News & Trends</h3>
                <p className="text-slate-400">Daily market intelligence on funding, models, and breakthroughs.</p>
              </div>
              <Link to="/insights" className="p-4 bg-slate-800 rounded-full hover:bg-slate-700 transition-colors">
                <ArrowRight size={24} />
              </Link>
            </div>

            {/* 4. Our Mission (Small) */}
            <div className="md:col-span-6 bg-slate-900/50 border border-slate-800 p-8 rounded-[2.5rem] flex flex-col md:flex-row items-center gap-6 hover:border-amber-500/50 transition-all">
              <div className="flex-1">
                <div className="w-12 h-12 bg-amber-500/20 text-amber-400 rounded-xl flex items-center justify-center mb-6">
                  <Target size={24} />
                </div>
                <h3 className="text-2xl font-bold mb-2">Our Mission</h3>
                <p className="text-slate-400">Building the bridge to a multi-agent future and universal access to talent.</p>
              </div>
              <Link to="/about-us" className="p-4 bg-slate-800 rounded-full hover:bg-slate-700 transition-colors">
                <ArrowRight size={24} />
              </Link>
            </div>

          </div>
        </div>
      </section>

      {/* --- STATS SECTION --- */}
      <section className="py-20 bg-white/5 backdrop-blur-sm border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <div>
            <div className="text-4xl font-black text-indigo-400 mb-2">500+</div>
            <div className="text-slate-500 uppercase tracking-widest text-xs font-bold">Global Roles</div>
          </div>
          <div>
            <div className="text-4xl font-black text-cyan-400 mb-2">200+</div>
            <div className="text-slate-500 uppercase tracking-widest text-xs font-bold">Elite Companies</div>
          </div>
          <div>
            <div className="text-4xl font-black text-emerald-400 mb-2">50k+</div>
            <div className="text-slate-500 uppercase tracking-widest text-xs font-bold">Learning Hours</div>
          </div>
          <div>
            <div className="text-4xl font-black text-amber-400 mb-2">Daily</div>
            <div className="text-slate-500 uppercase tracking-widest text-xs font-bold">Intelligence Updates</div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;